<script setup lang="ts">
import { computed } from 'vue';
import {
  historyWindows,
  type HistoryWindow,
  type ServerHistory,
} from '@atum-remote/protocol/server-inspection';
import { rate } from '../lib/server-resources.js';
import ResourceChart from './ResourceChart.vue';

const props = defineProps<{
  history: ServerHistory | undefined;
  window: HistoryWindow;
  /** False when the host predates recorded history. */
  supported: boolean;
  cores: number;
}>();
const emit = defineEmits<{ 'update:window': [window: HistoryWindow] }>();
const labels: Record<HistoryWindow, string> = {
  '15m': '15 minutes',
  '1h': 'hour',
  '6h': '6 hours',
  '24h': '24 hours',
};
const shown = computed(() => (props.history?.window === props.window ? props.history : undefined));
const percent = (v: number) => `${v.toFixed(v < 10 ? 1 : 0)}%`;
const load = (v: number) => v.toFixed(v < 10 ? 2 : 1);
const partial = computed(() => {
  const h = shown.value;
  if (!h || h.recordingSince <= h.start) return null;
  return `Recording since ${new Date(h.recordingSince).toLocaleString([], {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })}; earlier readings are not available.`;
});
const hasPressure = computed(
  () => !!shown.value?.series.memoryPressure.some((v) => v != null && v > 0),
);
</script>

<template>
  <section class="panel res-section" aria-labelledby="res-history-title">
    <div class="res-section-head">
      <div>
        <h3 id="res-history-title">History</h3>
        <p v-if="!supported">
          This host does not record history yet. Update ATUM Remote on the server to keep up to 24
          hours of readings.
        </p>
        <p v-else-if="partial">{{ partial }}</p>
        <p v-else>Averaged per point. Kept in memory on the host; resets when it restarts.</p>
      </div>
      <fieldset v-if="supported" class="segmented" aria-label="History window">
        <label v-for="option in historyWindows" :key="option"
          ><input
            type="radio"
            name="res-history-window"
            :value="option"
            :checked="window === option"
            @change="emit('update:window', option)"
          />{{ option }}</label
        >
      </fieldset>
    </div>
    <div v-if="supported" class="res-charts">
      <template v-if="shown">
        <ResourceChart
          title="CPU"
          :series="[{ label: 'CPU', values: shown.series.cpu, tone: 'in' }]"
          :start="shown.start"
          :step="shown.stepSeconds"
          :format="percent"
          :max="100"
          :window-label="labels[window]"
        />
        <ResourceChart
          title="Memory in use"
          :series="[{ label: 'Memory', values: shown.series.memory, tone: 'in' }]"
          :start="shown.start"
          :step="shown.stepSeconds"
          :format="percent"
          :max="100"
          :window-label="labels[window]"
        />
        <ResourceChart
          title="Load (1 min)"
          :series="[{ label: 'Load', values: shown.series.load, tone: 'in' }]"
          :start="shown.start"
          :step="shown.stepSeconds"
          :format="load"
          :floor="1"
          :reference="cores ? { value: cores, label: `${cores} CPUs` } : undefined"
          :window-label="labels[window]"
        />
        <ResourceChart
          title="Disk I/O"
          :series="[
            { label: 'Read', values: shown.series.diskRead, tone: 'in' },
            { label: 'Write', values: shown.series.diskWrite, tone: 'out' },
          ]"
          :start="shown.start"
          :step="shown.stepSeconds"
          :format="rate"
          :floor="1e6"
          :window-label="labels[window]"
        />
        <ResourceChart
          title="Network"
          :series="[
            { label: 'Receive', values: shown.series.netRx, tone: 'in' },
            { label: 'Send', values: shown.series.netTx, tone: 'out' },
          ]"
          :start="shown.start"
          :step="shown.stepSeconds"
          :format="rate"
          :floor="1e5"
          :window-label="labels[window]"
        />
        <ResourceChart
          v-if="hasPressure"
          title="Memory stall"
          :series="[{ label: 'Stalled', values: shown.series.memoryPressure, tone: 'in' }]"
          :start="shown.start"
          :step="shown.stepSeconds"
          :format="percent"
          :floor="10"
          :window-label="labels[window]"
        />
      </template>
      <p v-else class="res-muted res-charts-loading">Loading the last {{ labels[window] }}…</p>
    </div>
    <p v-if="supported && shown" class="res-legend">
      <span><span class="res-dot" data-tone="in" />Read / receive</span>
      <span><span class="res-dot" data-tone="out" />Write / send</span>
    </p>
  </section>
</template>
